import {
  ArrayField,
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  ChipField,
  Datagrid,
  DateField,
  Edit,
  FilterList,
  FilterListItem,
  linkToRecord,
  List,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  useRecordContext
} from "react-admin";
import { endOfYesterday, startOfMonth, startOfWeek, subMonths, subWeeks, } from 'date-fns';
import Button from '@material-ui/core/Button';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import React from "react";
import { Link } from 'react-router-dom';
import { Card as MuiCard, CardContent, Chip, Grid, makeStyles, Paper, Typography, withStyles } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { analyticsTrackEvent } from "../utils/segment";

const Card = withStyles(theme => ({
  root: {
    [theme.breakpoints.up('sm')]: {
      order: -1, // display on the left rather than on the right of the list
      width: '15em',
      marginRight: '1em',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}))(MuiCard);

const companySearch = (n) => {
  if (n && n.length > 2) {
    return {
      name__like: n,
    }
  }
}

const accessLevelChoices = [{ id: 0, name: "No access" }, { id: 1, name: "View Only" }, { id: 2, name: "Edit" }, {
  id: 3,
  name: "Admin"
}];

const useQuickFilterStyles = makeStyles(theme => ({
  chip: {
    marginBottom: theme.spacing(1),
  },
}));

const QuickFilter = ({ label }) => {
  const classes = useQuickFilterStyles();
  return <Chip className={classes.chip} label={label}/>;
};

const userFilters = [
  <TextInput label="Username" source="username__like" alwaysOn/>,
  <TextInput label="Firstname" source="first_name__like" alwaysOn/>,
  <TextInput label="LastName" source="last_name__like" alwaysOn/>,
  <TextInput label="Email" source="email__like" alwaysOn/>,
  <ReferenceInput perPage={200} label="Company" source="company_id" reference="Company" filterToQuery={companySearch} alwaysOn>
    <AutocompleteInput label="Comp" source="name"/>
  </ReferenceInput>,
  <TextInput label='User Id' source={"id"}/>,
  <SelectInput label='Super User' source={"role__like"}
               choices={[{ id: "employee", name: "Super User" }, { id: "", name: "Normal" }]}/>,
  <SelectInput label='Default Access Level' source={"global_access"}
               choices={accessLevelChoices}/>,
  <QuickFilter source="status" label="Requesting" defaultValue={'requesting'}/>,
  <BooleanInput source={'email_verified'}/>
];

const AsideFilter = () => (
  <Card>
    <CardContent>
      <FilterList label="Quick Filters">
        <FilterListItem label="Enabled Only" value={{ enabled: 1 }}/>
        <FilterListItem label="Disabled Only" value={{ enabled: 0 }}/>
        <FilterListItem label="Exclude Matrak staff" value={{ company_ids__neq: [1] }}/>
      </FilterList>
      <FilterList label="Date Created" icon={<AccessTimeIcon/>}>
        <FilterListItem
          label="Today"
          value={{
            created_at__gte: endOfYesterday().toISOString(),
            created_at__lte: undefined,
          }}
        />
        <FilterListItem
          label="This week"
          value={{
            created_at__gte: startOfWeek(new Date()).toISOString(),
            created_at__lte: undefined,
          }}
        />
        <FilterListItem
          label="Last week"
          value={{
            created_at__gte: subWeeks(startOfWeek(new Date()), 1).toISOString(),
            created_at__lte: startOfWeek(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="This month"
          value={{
            created_at__gte: startOfMonth(new Date()).toISOString(),
            created_at__lte: undefined,
          }}
        />
        <FilterListItem
          label="Last month"
          value={{
            created_at__gte: subMonths(startOfMonth(new Date()), 1).toISOString(),
            created_at__lte: startOfMonth(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="Earlier"
          value={{
            created_at__gte: undefined,
            created_at__lte: subMonths(startOfMonth(new Date()), 1).toISOString(),
          }}
        />
      </FilterList>
      <FilterList label="Last Login" icon={<AccessTimeIcon/>}>
        <FilterListItem
          label="Today"
          value={{
            last_login__gte: endOfYesterday().toISOString(),
            last_login__lte: undefined,
          }}
        />
        <FilterListItem
          label="This week"
          value={{
            last_login__gte: startOfWeek(new Date()).toISOString(),
            last_login__lte: undefined,
          }}
        />
        <FilterListItem
          label="Last week"
          value={{
            last_login__gte: subWeeks(startOfWeek(new Date()), 1).toISOString(),
            last_login__lte: startOfWeek(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="This month"
          value={{
            last_login__gte: startOfMonth(new Date()).toISOString(),
            last_login__lte: undefined,
          }}
        />
        <FilterListItem
          label="Last month"
          value={{
            last_login__gte: subMonths(startOfMonth(new Date()), 1).toISOString(),
            last_login__lte: startOfMonth(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="Earlier"
          value={{
            last_login__gte: undefined,
            last_login__lte: subMonths(startOfMonth(new Date()), 1).toISOString(),
          }}
        />
      </FilterList>
    </CardContent>
  </Card>
)

export const UserList = props => (
  <List {...props} filters={userFilters} aside={<AsideFilter/>} bulkActionButtons={[]}>
    <Datagrid rowClick="show">
      <TextField source="id"/>
      <TextField source="username"/>
      <TextField source="email"/>
      {/*<TextField source="company.name"/>*/}
      <ReferenceField label="Company" source={"company.id"} reference={'Company'} sortable={false} link="show">
        <TextField source="name"/>
      </ReferenceField>
      <BooleanField source="enabled" looseValue/>
      <SelectField source="global_access" choices={accessLevelChoices}/>
      <DateField source="created_at"/>
      <DateField source="updated_at"/>
    </Datagrid>
  </List>
);


const ProjectChipLink = (props) => {
  const userShowPage = linkToRecord('/Project', props.record.id, 'show');

  return <Link to={userShowPage} onClick={e => {
    return e.stopPropagation();
  }}>{props.children}</Link>;
}

const CopyLink=(props)=>{
  const { linkType } = props
  const record = useRecordContext();
  const link = record[linkType]
  console.log(link);
  return record ? (
    <Button
    onClick={()=>navigator.clipboard.writeText(link)}>
      <FileCopyIcon />
    </Button>
  ) : null;
}

const InvitationLink = props => {
  const record = useRecordContext();
  return (record?.first_name === '' && record?.last_name === '' && record?.username === '') ? (
    <>
      <TextField source="invitationLink" style={{ maxWidth: 230,wordBreak: 'break-all', whiteSpace:'pre-wrap', display: 'flex'}}/>
      <CopyLink linkType='invitationLink' />
    </>
  ) : (
    <Typography>User already signed up</Typography>
  );
};
  
const ActivationLink = props => {
  const record = useRecordContext();

  if (record?.email_verified) {
    return (
      <Typography>Email already verified</Typography>
    );
  } else if (record?.first_name === '' || record?.last_name === '' || record?.username === '') {
    return (
      <Typography>User hasn't yet signed up</Typography>
    );
  } else {
    return (
      <>
        <TextField style={{ maxWidth: 230,wordBreak: 'break-all', whiteSpace:'pre-wrap', display: 'flex'}} source="activationLink" />
        <CopyLink linkType='activationLink' />
      </>
    );
  }
};


export const UserView = props => (
  <Show {...props}>
    <SimpleShowLayout>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid item xs={12}>
          <Grid container spacing={2} justifyContent={"center"}>
            <Grid item xs={3}>
              <Paper elevation={3}>
                <SimpleShowLayout>
                  <Typography>User Properties</Typography>
                  <NumberField source="id"/>
                  <TextField source="username"/>
                  <TextField source="first_name"/>
                  <TextField source="last_name"/>
                  <TextField source="email"/>
                  <TextField source="phone_number"/>
                  <TextField source="job_title"/>
                  <TextField source="source"/>
                </SimpleShowLayout>
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <Paper elevation={3}>
                <SimpleShowLayout>
                  <Typography>User Status</Typography>
                  <BooleanField source="email_verified" looseValue/>
                  <BooleanField source="enabled" looseValue/>
                  <TextField source="status"/>
                  <TextField style={{ display: 'none' }} source="invitationLink" />
                  <InvitationLink/>
                  <TextField style={{ display: 'none' }} source="activationLink" />
                  <ActivationLink/>
                  <DateField source="created_at"/>
                  <DateField source="updated_at"/>
                  <DateField source="last_login"/>
                </SimpleShowLayout>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper elevation={3}>
                <SimpleShowLayout>
                  <Typography>User Access</Typography>
                  <ReferenceField label="Company" source={"company.id"} reference={'Company'} sortable={false}
                                  link="show">
                    <TextField source="name"/>
                  </ReferenceField>
                  <SelectField label="Default access for new projects" source="global_access"
                               choices={accessLevelChoices}/>
                  <ReferenceField label={"Projects"} source={"company.id"} reference={'Company'} link={false}>
                    <ArrayField source="projects">
                      <SingleFieldList>
                        <ReferenceField reference="Project" source="id" link={false}>
                          <ProjectChipLink>
                            <ChipField source="name" clickable={true}/>
                          </ProjectChipLink>
                        </ReferenceField>
                      </SingleFieldList>
                    </ArrayField>
                  </ReferenceField>
                </SimpleShowLayout>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

    </SimpleShowLayout>
  </Show>
  )

export const UserEditView = (props) => {
  return (
    <Edit
      title="Edit User"
      onSuccess={async({data}) => {
          const updates = {
            userId: data.id,
            email: data.email,
            userName: data.username,
            firstName: data.first_name,
            lastName: data.last_name,
            phone: data.phone,
            jobTitle: data.job_title,
            companyId: data.company.id,
            companyName: data.companyname,
          }
          analyticsTrackEvent("User Info Updated", "User", updates);
      }}
      {...props}
      undoable={false}
    >
      <SimpleForm>
        <TextInput disabled source="id"/>
        <TextInput source="username"/>
        <TextInput source="first_name"/>
        <TextInput source="last_name"/>
        <TextInput source="email"/>
        <TextInput source="phone_number"/>
        <SelectInput source="role" choices={[{id: '', name: 'Normal User'}, {id: 'employee', name: 'Super User'}]}/>
        <TextInput source="job_title"/>
        <ReferenceInput perPage={200} filterToQuery={companySearch} label="Company" source="company_id" reference="Company">
          <AutocompleteInput source="name"/>
        </ReferenceInput>
      </SimpleForm>
    </Edit>
  )
}
